import { useRef, useState, type ChangeEvent, type ReactElement } from 'react';

import { PHOTO_ACCEPT, PHOTO_MAX_BYTES } from '@/features/members/api';
import { ApiError } from '@/lib/api/http';

/**
 * 顔写真の表示とアップロード（マイプロフィール・会員詳細で使い回す / D-053）。
 * 実体はMinIOにあり、`photoUrl` には短命な閲覧URLが入る。
 */
export function MemberPhotoField({
  photoUrl,
  name,
  editable,
  onUpload,
  onDelete,
}: {
  photoUrl: string | null;
  /** 代替テキスト用（「〇〇さんの顔写真」） */
  name: string;
  editable: boolean;
  onUpload: (file: File) => Promise<void>;
  onDelete: () => Promise<void>;
}): ReactElement {
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSelect(event: ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = event.target.files?.[0];
    // 同じファイルを選び直せるように毎回クリアする
    event.target.value = '';
    if (!file) return;

    setError(null);
    if (file.size > PHOTO_MAX_BYTES) {
      setError(`画像サイズが上限（${PHOTO_MAX_BYTES / (1024 * 1024)}MB）を超えています。`);
      return;
    }

    setBusy(true);
    try {
      await onUpload(file);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '画像をアップロードできませんでした。');
    } finally {
      setBusy(false);
    }
  }

  async function onRemove(): Promise<void> {
    setError(null);
    setBusy(true);
    try {
      await onDelete();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '画像を削除できませんでした。');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex items-start gap-4">
      <PhotoPreview photoUrl={photoUrl} name={name} />

      {editable && (
        <div>
          <input
            ref={inputRef}
            type="file"
            accept={PHOTO_ACCEPT}
            onChange={(event) => void onSelect(event)}
            className="hidden"
            aria-label="顔写真を選択"
          />
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => inputRef.current?.click()}
              className="btn-secondary"
            >
              {busy ? '処理中…' : photoUrl ? '写真を変更' : '写真を登録'}
            </button>
            {photoUrl && (
              <button
                type="button"
                disabled={busy}
                onClick={() => void onRemove()}
                className="btn-secondary"
              >
                削除
              </button>
            )}
          </div>
          <p className="mt-1 text-xs text-wptext-muted">
            JPEG・PNG・WebP、{PHOTO_MAX_BYTES / (1024 * 1024)}MBまで
          </p>
          {error && <p className="mt-1 text-wpnotice-error">{error}</p>}
        </div>
      )}
    </div>
  );
}

/** 表示のみ（会員一覧のサムネイル等） */
export function PhotoPreview({
  photoUrl,
  name,
  size = 96,
}: {
  photoUrl: string | null;
  name: string;
  size?: number;
}): ReactElement {
  if (!photoUrl) {
    // 写真が無い場合は頭文字入りの●で代替する（「写真なし」の文字箱より一覧が読みやすい / D-112）
    return (
      <div
        style={{ width: size, height: size, fontSize: Math.max(11, Math.floor(size * 0.4)) }}
        aria-label={`${name}さん（写真未登録）`}
        title={`${name}（写真未登録）`}
        className="flex shrink-0 select-none items-center justify-center rounded-full bg-wpborder font-semibold text-wptext-strong"
      >
        {name.trim().charAt(0) || '●'}
      </div>
    );
  }
  return (
    <img
      src={photoUrl}
      alt={`${name}さんの顔写真`}
      width={size}
      height={size}
      style={{ width: size, height: size }}
      className="shrink-0 rounded-sm border border-wpborder object-cover"
    />
  );
}
